import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useSessionTokenHash } from '@/hooks/useSessionTokenHash';
import {
  useUserSessions,
  useRevokeSession,
  useRevokeOtherSessions,
  useTrustSession,
} from '@/hooks/useUserSessions';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import toast from 'react-hot-toast';
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Shield,
} from 'lucide-react';
import type { UserSession } from '@/types/sessions';

function getDeviceIcon(deviceType: string | null) {
  switch (deviceType) {
    case 'mobile':
      return Smartphone;
    case 'tablet':
      return Tablet;
    case 'desktop':
      return Monitor;
    default:
      return Globe;
  }
}

function formatLastActive(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 1000 / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

function SessionCard({
  session,
  isCurrent,
  onRevoke,
  onTrust,
  revoking,
  trusting,
}: {
  session: UserSession;
  isCurrent: boolean;
  onRevoke: (id: string) => void;
  onTrust: (id: string) => void;
  revoking: boolean;
  trusting: boolean;
}) {
  const { t } = useTranslation();
  const DeviceIcon = getDeviceIcon(session.device_type);

  return (
    <div className="rounded-lg border border-gray-200 bg-card-light p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <div className="flex items-start justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
            <DeviceIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900 dark:text-white">
                {session.device_name || 'Unknown Device'}
              </h3>
              {isCurrent && (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {t('session.current', 'Current')}
                </span>
              )}
              {session.is_trusted && (
                <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            
            <div className="mt-1 space-y-1 text-sm text-gray-500 dark:text-gray-400">
              {session.browser && session.os && (
                <div className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  <span>
                    {session.browser} · {session.os}
                  </span>
                </div>
              )}
              
              {session.ip_address && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{session.ip_address}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {t('session.last_active', 'Last active')}: {formatLastActive(session.last_activity_at)}
                </span>
              </div>

              {session.is_trusted && session.trusted_until && (
                <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>
                    {t('session.trusted_until', 'Trusted until')}:{' '}
                    {new Date(session.trusted_until).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isCurrent && (
          <div className="flex gap-2">
            {!session.is_trusted && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onTrust(session.id)}
                disabled={trusting}
              >
                {trusting ? t('session.trusting', 'Trusting...') : t('session.trust', 'Trust')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRevoke(session.id)}
              disabled={revoking}
            >
              {revoking ? t('session.revoking', 'Revoking...') : t('session.revoke', 'Revoke')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AccountSessions() {
  const { t } = useTranslation();
  const { user, session } = useAuth();
  const { data: sessions, isLoading, error } = useUserSessions(user?.id);
  const revokeSession = useRevokeSession();
  const revokeOtherSessions = useRevokeOtherSessions();
  const trustSession = useTrustSession();

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [trustingId, setTrustingId] = useState<string | null>(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const currentSessionToken = useSessionTokenHash(session?.access_token);

  const handleRevoke = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await revokeSession.mutateAsync({
        sessionId,
        reason: 'User manually revoked session',
      });
      toast.success(t('session.revoked', 'Session revoked successfully'));
    } catch (err) {
      console.error('Failed to revoke session:', err);
      toast.error(t('session.revoke_error', 'Failed to revoke session'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleTrust = async (sessionId: string) => {
    setTrustingId(sessionId);
    try {
      await trustSession.mutateAsync({ sessionId, durationDays: 30 });
      toast.success(t('session.trusted', 'Device trusted for 30 days'));
    } catch (err) {
      console.error('Failed to trust session:', err);
      toast.error(t('session.trust_error', 'Failed to trust device'));
    } finally {
      setTrustingId(null);
    }
  };

  const handleRevokeOthers = async () => {
    if (!currentSessionToken) {
      toast.error(t('session.no_current', 'Current session not found'));
      return;
    }

    if (!window.confirm(t('session.revoke_others_confirm', 'Are you sure you want to log out all other devices?'))) {
      return;
    }

    setRevokingOthers(true);
    try {
      const count = await revokeOtherSessions.mutateAsync(currentSessionToken);
      toast.success(
        t('session.revoked_others', `Logged out ${count} other ${count === 1 ? 'session' : 'sessions'}`),
      );
    } catch (err) {
      console.error('Failed to revoke other sessions:', err);
      toast.error(t('session.revoke_others_error', 'Failed to log out other devices'));
    } finally {
      setRevokingOthers(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('session.title', 'Active Sessions')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('session.subtitle', 'Manage your active sessions and devices')}
          </p>
        </header>
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('session.title', 'Active Sessions')}
          </h1>
        </header>
        <ErrorAlert message={error.message} />
      </div>
    );
  }

  const otherSessions = sessions?.filter((s) => s.session_token !== currentSessionToken) ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('session.title', 'Active Sessions')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('session.subtitle', 'Manage your active sessions and devices')}
        </p>
      </header>

      {otherSessions.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={handleRevokeOthers}
            disabled={revokingOthers}
          >
            <XCircle className="mr-2 h-4 w-4" />
            {revokingOthers
              ? t('session.revoking_others', 'Logging out...')
              : t('session.revoke_all_others', 'Log out all other devices')}
          </Button>
        </div>
      )}

      <section className="space-y-4">
        {sessions?.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-card-light p-8 text-center dark:border-gray-700 dark:bg-card-dark">
            <p className="text-gray-500 dark:text-gray-400">
              {t('session.no_sessions', 'No active sessions found')}
            </p>
          </div>
        ) : (
          sessions?.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              isCurrent={session.session_token === currentSessionToken}
              onRevoke={handleRevoke}
              onTrust={handleTrust}
              revoking={revokingId === session.id}
              trusting={trustingId === session.id}
            />
          ))
        )}
      </section>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/40 dark:bg-blue-900/20">
        <div className="flex gap-3">
          <Shield className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-100">
              {t('session.security_tip', 'Security Tip')}
            </p>
            <p className="mt-1 text-blue-800 dark:text-blue-200">
              {t(
                'session.security_message',
                'If you see any suspicious sessions, revoke them immediately and change your password.',
              )}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
